import { Link, Navigate, Outlet, useLocation } from 'react-router';

import { Button, Spinner, buttonStyles } from '@psspl/ui';

import { isSignedOut, useAuth, type SessionEndReason } from '../auth/auth-context';
import { CompanyLogo } from '../components/brand/company-logo';
import { FORBIDDEN_DETAIL, StatusPage } from '../components/common/status-page';
import { homeRouteFor } from '../lib/access';
import { ROUTES } from '../lib/routes';
import type { Capability } from '../types';

/**
 * Gate for authenticated routes.
 *
 * This is UX, not security. `docs/requirements.md` NFR-01: the API is the only
 * authority. The guard only avoids rendering a screen the API will refuse.
 *
 * It never renders protected content before the session is known: `loading` and
 * `unavailable` each have their own full-page state, so a hard refresh neither
 * flashes the portal nor bounces a signed-in user to /login.
 */
export function RequireAuth() {
  const { status, endReason } = useAuth();

  switch (status) {
    case 'loading':
      return <SessionCheck />;
    case 'unavailable':
      return <SessionUnavailable />;
    case 'unauthenticated':
      return <RedirectToLogin endReason={endReason} />;
    case 'authenticated':
      return <Outlet />;
  }
}

/**
 * Gate for the admin area (nested inside `RequireAuth`).
 *
 * Opens for any user the server gave an admin capability (`lib/access.ts`): in
 * practice SUPER_ADMIN and ADMIN. Everyone else is sent to the catalogue, which is
 * outside this guard, so the redirect cannot loop. Presentation only: the API
 * returns 403 for every admin request from anyone without the capability, whatever
 * the browser renders.
 */
export function RequireAdminArea() {
  const { canUseAdminArea } = useAuth();

  if (!canUseAdminArea) {
    return <Navigate to={ROUTES.projects} replace />;
  }

  return <Outlet />;
}

/**
 * Gate for one admin section, by the capability its endpoints require (the Users
 * section needs `users:manage`). Same 403 and same caveat as `RequireAdminArea`.
 */
export function RequireCapability({ capability }: { readonly capability: Capability }) {
  const { can } = useAuth();

  if (!can(capability)) {
    return <StatusPage code="403" detail={FORBIDDEN_DETAIL} />;
  }

  return <Outlet />;
}

/** `/` → the signed-in user's home area: admin for administrators, else projects. */
export function HomeRedirect() {
  const { user } = useAuth();

  return <Navigate to={user === null ? ROUTES.login : homeRouteFor(user)} replace />;
}

/**
 * Sends the user to sign in, remembering where they were headed.
 *
 * `state.from` survives an expired session or a deep link. It is dropped after a
 * deliberate sign-out, so the next person to sign in on this browser starts at the
 * catalogue rather than on the previous user's last screen. `/login` itself is
 * outside this guard, so the redirect cannot loop.
 */
function RedirectToLogin({ endReason }: { readonly endReason: SessionEndReason | null }) {
  const location = useLocation();

  return (
    <Navigate
      to={ROUTES.login}
      replace
      state={isSignedOut(endReason) ? null : { from: location.pathname + location.search }}
    />
  );
}

function SessionCheck() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-surface-base px-5"
    >
      <CompanyLogo size="md" alt="" />
      <div className="flex items-center gap-2.5 text-sm text-ink-muted">
        <Spinner size="sm" className="text-brand-400" label="" />
        Checking your session…
      </div>
    </div>
  );
}

/**
 * The API could not be reached while restoring the session.
 *
 * Deliberately not treated as "signed out": that would send a signed-in user to the
 * sign-in form for a network blip, and signing in would fail the same way.
 */
function SessionUnavailable() {
  const { retry } = useAuth();

  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface-base px-5 py-16">
      <div className="flex w-full max-w-md flex-col items-center gap-5 rounded-lg border border-line-subtle bg-surface-raised px-6 py-8 text-center">
        <CompanyLogo size="md" />
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold tracking-tight text-ink-primary">
            We can’t reach Prisco right now
          </h1>
          <p className="text-sm leading-relaxed text-ink-secondary">
            Your session could not be checked because the service did not respond. Check your
            connection, then try again.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={retry}>Try again</Button>
          <Link to={ROUTES.login} className={buttonStyles({ variant: 'secondary' })}>
            Go to sign in
          </Link>
        </div>
      </div>
    </main>
  );
}
